import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Utensils,
  Plus,
  Calculator,
  Calendar,
  X,
  List,
  Book,
  Flame,
  Drumstick,
  Cookie,
  Fish,
} from "lucide-react";
import api from "services/api";
import useStore from "services/store";
import toast from "react-hot-toast";

export function MealDashboard() {
  const { user, metrics } = useStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("mealPlan");
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mealPlan, setMealPlan] = useState(null);

  useEffect(() => {
    getMealPlan();
  }, []);

  const getMealPlan = async () => {
    try {
      const response = await api.get("/meal-plans/");
      if (!response.ok) {
        toast.error(response.message);
        return;
      }
      setMealPlan(response.data)
    } catch (error) {
      console.log(error);
      toast.error("Failed to load meal plan");
    }
  };

  const handleMealSelect = (meal) => {
    setSelectedMeal(meal);
    setIsModalOpen(true);
  };

  const handleDeletePlan = async () => {
    const confirm = window.confirm("Are you sure you want to delete your meal plan?");
    if (!confirm) {
      return;
    }
    try {
      const response = await api.delete("/meal-plans/delete", {
        id: mealPlan._id,
      });
      if (!response.ok) {
        toast.error(response.message || "Failed to delete meal plan");
        return;
      }
      toast.success("Meal plan deleted successfully");
    } catch (error) {
      console.log(error);
      toast.error("An error occurred while deleting the meal plan");
    }
  };

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-black mb-2">
            Meal Dashboard
          </h1>
          <p className="text-gray-600">Track your nutrition and meal plans</p>
        </div>

        {/* Macro Metrics Card */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold text-black mb-4 flex items-center">
            <Utensils className="mr-2 text-blue-500" size={20} />
            Daily Macro Goals
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MacroCard
              title="Calories"
              value={metrics?.daily_calories?.toFixed(0) || "N/A"}
              unit="kcal"
              color="text-red-500"
              icon={<Flame size={20} />}
            />
            <MacroCard
              title="Protein"
              value={metrics?.macro_distribution?.protein?.toFixed(0) || "N/A"}
              unit="g"
              color="text-orange-500"
              icon={<Drumstick size={20} />}
            />
            <MacroCard
              title="Carbs"
              value={metrics?.macro_distribution?.carbs?.toFixed(0) || "N/A"}
              unit="g"
              color="text-yellow-500"
              icon={<Cookie size={20} />}
            />
            <MacroCard
              title="Fats"
              value={metrics?.macro_distribution?.fats?.toFixed(0) || "N/A"}
              unit="g"
              color="text-purple-500"
              icon={<Fish size={20} />}
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("mealPlan")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "mealPlan"
                    ? "border-gray-500 text-gray-500"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Calendar className="inline mr-2" />
                Meal Plan
              </button>
              <button
                onClick={() => setActiveTab("logMeal")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "logMeal"
                    ? "border-gray-500 text-gray-500"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Plus className="inline mr-2" />
                Log a Meal
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "mealPlan" ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    Your Weekly Meal Plan
                  </h3>

                  {mealPlan?.plan && (
                    <button
                      onClick={handleDeletePlan}
                      className="text-white hover:text-gray-300 text-sm font-medium"
                    >
                      Delete Plan
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  {mealPlan?.plan ? (
                    mealPlan.plan.map((dayPlan) => (
                      <MealPlanDay
                        key={dayPlan._id.$oid}
                        day={dayPlan.day}
                        meals={dayPlan.meals}
                        onMealSelect={handleMealSelect}
                      />
                    ))
                  ) : (
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-black text-center">
                      <h3 className="text-lg font-semibold text-black mb-4">
                        No Meal Plan Found
                      </h3>
                      <p className="text-black mb-6">
                        Create a personalized meal plan to get started
                      </p>
                      <button
                        onClick={() => navigate("/create-meal-plan")}
                        className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        Create Meal Plan
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <ComingSoonCard />
            )}
          </div>
        </div>
      </div>

      {/* Meal Modal */}
      <MealModal
        meal={selectedMeal}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMeal(null);
        }}
      />
    </div>
  );
}

const MacroCard = ({ title, value, unit, color, icon }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
    <div className="flex items-center justify-between mb-2">
      <div className={`text-2xl ${color}`}>{icon}</div>
      <div className="text-right">
        <div className="text-lg font-bold text-black">{value || "N/A"}</div>
        <div className="text-sm text-gray-600">{unit}</div>
      </div>
    </div>
    <div className="text-xs text-gray-500">{title}</div>
  </div>
);

const ComingSoonCard = () => (
  <div className="bg-white rounded-lg p-8 text-center border border-gray-200 shadow-sm">
    <div className="text-black text-4xl mb-4">
      <Plus className="mx-auto" size={48} />
    </div>
    <h3 className="text-xl font-semibold text-black mb-2">Coming Soon!</h3>
    <p className="text-black mb-4">
      We're working hard to bring you the meal logging feature. Stay tuned for updates!
    </p>
    <div className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-full text-sm font-medium">
      <Calendar className="mr-2" size={16} />
      In Development
    </div>
  </div>
);

const MealPlanDay = ({ day, meals, onMealSelect }) => {
  const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 mb-4">
      <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
        <Calendar className="mr-2 text-blue-500" size={20} />
        {dayNames[day - 1]}
      </h3>
      <div className="space-y-3">
        {meals.map((meal) => (
          <div
            key={meal._id.$oid}
            className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Utensils className="text-green-500 mr-2" size={18} />
                <button
                  onClick={() => onMealSelect(meal)}
                  className="font-medium text-black hover:text-gray-600 transition-colors cursor-pointer text-left"
                >
                  {meal.name}
                </button>
              </div>
              <div className="text-sm text-gray-600">
                {meal.total_macros.calories} cal
              </div>
            </div>
            <div className="flex gap-4 mt-2 text-xs text-gray-500">
              <span>Protein: {meal.total_macros.protein}g</span>
              <span>Carbs: {meal.total_macros.carbs}g</span>
              <span>Fats: {meal.total_macros.fats}g</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MealModal = ({ meal, isOpen, onClose }) => {
  if (!isOpen || !meal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Utensils className="text-blue-500 mr-3" size={24} />
            <h2 className="text-xl font-bold text-black">{meal.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Macro Summary */}
          <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
              <Calculator className="mr-2 text-blue-500" size={20} />
              Nutritional Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MacroCard
                title="Protein"
                value={meal.total_macros.protein}
                unit="g"
                color="text-orange-500"
                icon={<Drumstick size={20} />}
              />
              <MacroCard
                title="Carbs"
                value={meal.total_macros.carbs}
                unit="g"
                color="text-yellow-500"
                icon={<Cookie size={20} />}
              />
              <MacroCard
                title="Fats"
                value={meal.total_macros.fats}
                unit="g"
                color="text-purple-500"
                icon={<Fish size={20} />}
              />
              <MacroCard
                title="Calories"
                value={meal.total_macros.calories}
                unit="kcal"
                color="text-red-500"
                icon={<Flame size={20} />}
              />
            </div>
          </div>

          {/* Recipe */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
              <Book className="mr-2 text-blue-500" size={20} />
              Recipe Instructions
            </h3>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-gray-600 leading-relaxed">{meal.recipe}</p>
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
              <List className="mr-2 text-blue-500" size={20} />
              Ingredients ({meal.ingredients.length})
            </h3>
            <div className="space-y-3">
              {meal.ingredients.map((ingredient) => (
                <div
                  key={ingredient._id.$oid}
                  className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-black">
                      {ingredient.name}
                    </h4>
                    <span className="text-sm text-gray-600">
                      {ingredient.amount}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
